/// <amd-module name="Repos/Editor/SearchContextMenuItemExtension" />
import { IProjectRepositoryInformationService } from "Repos/Common/ProjectRepositoryInformationService";
import * as Resources from "Repos/Editor/Resources";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { onClickFPS } from "VSS/Platform/FPS";
import { routeUrl } from "VSS/Platform/Location";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

const searchProjectRoute = "ms.vss-search-web.project-route";
const codeEntityTypeName: string = "code";
const projectFilters: string = "ProjectFilters";
const repoFilters: string = "RepositoryFilters";
const filterValueStart = "{";
const filterValueEnd = "}";
const codeSearchContextMenuItemId: string = "72802cd9-9396-4276-9059-8c5e227cd8be";
const codesearchSearchForDefinitionContextMenuItemId: string = "ada3e19f-82ea-47a9-9585-e4ac3088b9e1";
const codesearchSearchForReferencesContextMenuItemId: string = "08350060-51be-4155-9297-58633a8200e1";
const searchCodeEntityContribution: string = "ms.vss-code-web.versioncontrol-project-search-provider";

/**
 * Creates a codesearch specific action item to be shown in the monaco editor pop up on right-click.
 */
export function getSearchContextMenuItems(pageContext: IVssPageContext): monaco.editor.IActionDescriptor[] {
    const searchContextMenuItems: Array<monaco.editor.IActionDescriptor> = [];

    if (pageContext.getService<IVssContributionService>("IVssContributionService").getContribution(searchCodeEntityContribution)) {
        searchContextMenuItems.push(
            getActionDescriptor(pageContext, codeSearchContextMenuItemId, Resources.CodeSearchToTextLabel, "1_codesearch/1_termsearch")
        );

        searchContextMenuItems.push(
            getActionDescriptor(
                pageContext,
                codesearchSearchForDefinitionContextMenuItemId,
                Resources.CodeSearchToDefinitionLabel,
                "1_codesearch/2_definitionsearch"
            )
        );

        searchContextMenuItems.push(
            getActionDescriptor(
                pageContext,
                codesearchSearchForReferencesContextMenuItemId,
                Resources.CodeSearchToReferenceLabel,
                "1_codesearch/3_referencesearch"
            )
        );
    }

    return searchContextMenuItems;
}

function getActionDescriptor(pageContext: IVssPageContext, id: string, label: string, contextMenuGroupId: string): monaco.editor.IActionDescriptor {
    return {
        id: id,
        label: label,
        contextMenuGroupId: contextMenuGroupId,
        run: editor => searchContextMenuItemClickAction(pageContext, editor, id)
    };
}

/**
 * Executes when "search" context menu item is clicked. This opens up a new window to display code search results.
 */
function searchContextMenuItemClickAction(pageContext: IVssPageContext, editor: monaco.editor.ICodeEditor, contextMenuItemId: string): void {
    let searchText: string = "";
    const selection = editor.getSelection() as monaco.Selection;
    const model = editor.getModel() as monaco.editor.ITextModel;
    const selectionEndLineNumber: number = Math.max(selection.startLineNumber, selection.endLineNumber);
    const selectionStartLineNumber: number = Math.min(selection.startLineNumber, selection.endLineNumber);
    const selectionStartColumnNumber: number = Math.min(selection.startColumn, selection.endColumn);
    const selectionEndColumnNumber: number = Math.max(selection.startColumn, selection.endColumn);

    if (selectionEndLineNumber > selectionStartLineNumber || selectionEndColumnNumber > selectionStartColumnNumber) {
        searchText = model.getValueInRange(selection);
    } else {
        // There is no selection. The cursor focuses a word.
        const wordAtPosition = model.getWordAtPosition({
            lineNumber: selectionStartLineNumber,
            column: selectionStartColumnNumber
        });

        // wordAtPosition is null if cursor is not placed over any text. e.g. end of line.
        if (wordAtPosition) {
            searchText = wordAtPosition.word;
        }
    }

    searchText = searchText.trim();

    if (searchText !== "") {
        // Log contextual menu click telemetry.
        logTelemetryInformation(pageContext, contextMenuItemId);
        routeFromReposHub(pageContext, searchText, contextMenuItemId);
    }
}

function logTelemetryInformation(pageContext: IVssPageContext, eventId: string): void {
    let actionName: string = "";

    switch (eventId) {
        case codeSearchContextMenuItemId:
            actionName = "TermSearchContextMenuItemAction";
            break;

        case codesearchSearchForDefinitionContextMenuItemId:
            actionName = "DefinitionSearchContextMenuItemAction";
            break;

        case codesearchSearchForReferencesContextMenuItemId:
            actionName = "ReferencesSearchContextMenuItemAction";
            break;

        // controll will never reach to default case
        // as we check if the context item clicked is a search related context menu item.
        default:
            break;
    }
    const telemetryService: IVssTelemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    telemetryService.publishEvent("webaccess.search", "CodeSearchPortal", { actionName: actionName });
}

function getSearchText(text: string, eventId: string): string {
    let searchText: string = "";
    // Search text should be treated as phrase
    if (text.trim().split(" ").length > 1) {
        text = '"'.concat(text).concat('"');
    }

    switch (eventId) {
        case codeSearchContextMenuItemId:
            searchText = text;
            break;

        case codesearchSearchForDefinitionContextMenuItemId:
            searchText = "def:".concat(text);
            break;

        case codesearchSearchForReferencesContextMenuItemId:
            searchText = "ref:".concat(text);
            break;

        // controll will never reach to default case
        // as we check if the context item clicked is a search related context menu item.
        default:
            break;
    }

    return searchText;
}

function routeFromReposHub(pageContext: IVssPageContext, searchText: string, contextualMenuItemId: string): void {
    const pageData = getTFSPageData(pageContext);
    const project = pageData && pageData.project && pageData.project.name;

    let contextFilters: string = project ? projectFilters + filterValueStart + project + filterValueEnd : "";

    // Determine which repository should we be searching.
    const routingService = pageContext.getService<IProjectRepositoryInformationService>("IProjectRepositoryInformationService");
    const vcNavigationData = routingService.getData();

    if (vcNavigationData) {
        const repo: string = vcNavigationData.defaultRepoIsGit ? vcNavigationData.defaultGitRepoName : `$/${project}`;
        contextFilters = contextFilters.concat(repoFilters + filterValueStart + repo + filterValueEnd);
    }

    const urlParams = {
        type: codeEntityTypeName,
        text: getSearchText(searchText, contextualMenuItemId),
        filters: contextFilters,
        project: project ? project : ""
    };

    // route to search
    const url = routeUrl(pageContext, searchProjectRoute, urlParams);

    onClickFPS(pageContext, url, true);
}
